import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import ActionCenter from '../../components/coaching/ActionCenter';
import toast from 'react-hot-toast';

const CoachingDashboard = () => {
  const { coachingId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useRealtime();
  
  const [loading, setLoading] = useState(true);
  const [coachingData, setCoachingData] = useState(null);

  // Check if user has access to coaching dashboard
  const hasAccess = userProfile?.role === 'admin' || 
                   userProfile?.role === 'founder' || 
                   userProfile?.coachingId === coachingId ||
                   (userProfile?.role === 'teacher' && userProfile?.coachingId === coachingId) ||
                   coachingId === 'test-coaching-id';

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth/login');
      return;
    }

    if (!hasAccess) {
      toast.error('You do not have access to this coaching dashboard');
      navigate('/dashboard');
      return;
    }

    setCoachingData({
      id: coachingId,
      name: userProfile?.coachingName || 'Coaching Center',
      description: 'Daily prioritized actions and insights'
    });
    setLoading(false);
  }, [currentUser, hasAccess, coachingId, navigate, userProfile]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Exit</span>
              </button>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  {coachingData?.name}
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  AI Action Center
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {userProfile?.displayName || currentUser?.email}
                </p>
                <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400 font-bold">
                  {userProfile?.role || 'Admin'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Simplified to just Action Center */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <ActionCenter coachingId={coachingId} />
      </div>
    </div>
  );
};

export default CoachingDashboard;